import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, Clock } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { themeVars, SafeImg } from "../components/StoreKit";
import { resolveCta } from "../lib/cta";
import { storePath } from "../lib/storeUrl";

const pairs = (s = "") => String(s).split("\n").map((x) => x.trim()).filter(Boolean).map((l) => l.split("|").map((x) => (x || "").trim()));

function Faq({ items, radius }) {
  const [open, setOpen] = useState(0);
  if (!items.length) return null;
  return (
    <div className="space-y-3 mt-10" data-testid="service-faq">
      <h2 className="fh text-2xl tracking-tighter" style={{ color: "var(--sp)" }}>Frequently Asked Questions</h2>
      {items.map(([q, a], i) => (
        <div key={i} className="bg-white border border-black/5 overflow-hidden" style={{ borderRadius: radius }}>
          <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between px-6 py-4 text-left">
            <span className="font-bold text-sm" style={{ color: "var(--sp)" }}>{q}</span>
            <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} style={{ color: "var(--sa)" }} />
          </button>
          {open === i && <p className="px-6 pb-4 text-sm" style={{ color: "var(--st)", opacity: 0.7 }}>{a}</p>}
        </div>
      ))}
    </div>
  );
}

/* Theme-native service details page — renders a service card from the website's published editor doc. */
export default function ServiceDetails({ slugOverride }) {
  const { slug: paramSlug, sid } = useParams();
  const slug = slugOverride || paramSlug;
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!slug) return;
    api.get(`/public/store/${slug}`).then((r) => setData(r.data)).catch(() => setErr("notfound"));
  }, [slug]);

  const store = data?.store || {};
  const theme = data?.theme || {};
  const sections = data?.content?.sections || [];
  const svc = sections.filter((s) => s.type === "services" && !s.hidden)
    .flatMap((s) => (Array.isArray(s.props?.cards) ? s.props.cards : []))
    .find((c) => c.id === sid);

  useEffect(() => {
    if (svc?.name && store.name) document.title = `${svc.name} · ${store.name}`;
  }, [svc?.name, store.name]);

  const vars = themeVars(theme.colors || {}, theme);
  const radius = theme.radius || "10px";

  if (err || (data && !svc)) {
    return (
      <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className="min-h-screen bg-[var(--sbg)] text-[var(--st)] flex items-center justify-center px-4" data-testid="service-not-found">
        <div className="text-center">
          <p className="fh text-3xl tracking-tighter" style={{ color: "var(--sp)" }}>Service not found</p>
          <p className="text-sm opacity-60 mt-2">This service may have been removed or the link is incorrect.</p>
          <Link to={storePath(slug, "/")} data-testid="service-back-home"
            className="inline-block mt-6 px-7 py-3 rounded-full font-bold text-sm text-white" style={{ backgroundColor: "var(--sp)" }}>
            Back to {store.name || "Home"}
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;
  }

  const cta = resolveCta(svc, { slug });
  const includes = (Array.isArray(svc.includes) ? svc.includes : []).filter(Boolean);
  const faq = pairs(svc.faq);

  return (
    <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className="min-h-screen bg-[var(--sbg)] text-[var(--st)]" data-testid="service-details-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to={storePath(slug, "/")} data-testid="service-back-link"
          className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to {store.name || "website"}
        </Link>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mt-8 items-start">
          <div>
            {svc.image
              ? <SafeImg src={fileUrl(svc.image)} alt={svc.name} className="w-full aspect-[4/3] object-cover shadow-2xl" style={{ borderRadius: `calc(${radius} * 2)` }} />
              : <div className="w-full aspect-[4/3] flex items-center justify-center text-white text-sm font-bold" style={{ borderRadius: `calc(${radius} * 2)`, backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>{svc.name}</div>}
          </div>
          <div>
            {svc.duration && (
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest mb-4"
                style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>
                <Clock className="h-3 w-3" /> {svc.duration}
              </span>
            )}
            <h1 className="fh text-4xl sm:text-5xl tracking-tighter leading-[1.02]" style={{ color: "var(--sp)" }} data-testid="service-name">{svc.name}</h1>
            {(svc.price || svc.offerPrice) && (
              <div className="flex items-baseline gap-3 mt-5" data-testid="service-price">
                {svc.offerPrice
                  ? <><span className="font-display font-extrabold text-3xl" style={{ color: "var(--sa)" }}>{svc.offerPrice}</span><span className="text-lg line-through opacity-40">{svc.price}</span></>
                  : <span className="font-display font-extrabold text-3xl" style={{ color: "var(--sp)" }}>{svc.price}</span>}
              </div>
            )}
            {svc.description && <p className="mt-5 text-base sm:text-lg leading-relaxed opacity-75">{svc.description}</p>}
            {svc.details && <p className="mt-4 text-sm sm:text-base leading-relaxed opacity-65 whitespace-pre-line">{svc.details}</p>}
            {includes.length > 0 && (
              <div className="mt-7 bg-white border border-black/5 p-6" style={{ borderRadius: radius }} data-testid="service-includes">
                <p className="text-[11px] uppercase tracking-widest font-extrabold mb-4" style={{ color: "var(--sa)" }}>What's included</p>
                <div className="space-y-2.5">
                  {includes.map((f) => (
                    <p key={f} className="text-sm flex items-start gap-2.5 opacity-85">
                      <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--sa)" }} /> {f}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {svc.buttonText && (svc.buttonType || "none") !== "none" && (
              <div className="mt-8">
                {cta
                  ? <a href={cta.href} target={cta.external ? "_blank" : undefined} rel={cta.external ? "noreferrer" : undefined}
                      data-testid="service-cta"
                      className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full font-bold text-sm text-white shadow-lg active:scale-95 transition-transform"
                      style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>{svc.buttonText}</a>
                  : <span className="inline-flex px-9 py-4 rounded-full font-bold text-sm text-white" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>{svc.buttonText}</span>}
              </div>
            )}
            <Faq items={faq} radius={radius} />
          </div>
        </div>
      </div>
    </div>
  );
}
